import type { OpsMateCustomer, PromptedDraftResponse } from "../types";

const OPSMATE_API_BASE = "https://opsmate.soundsgood.as/api";
const WEBHOOK_BASE = "https://traftec.ngrok.app";

export const OPSMATE_PROFILE_BASE = "https://opsmate.soundsgood.as";

type TokenRefresher = () => Promise<string | null>;

export async function lookupCustomerByEmail(
  email: string,
  token: string,
  refreshToken: TokenRefresher,
  isRetry = false
): Promise<OpsMateCustomer | null> {
  const response = await fetch(`${OPSMATE_API_BASE}/users/null`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    if (response.status === 401 && !isRetry) {
      const newToken = await refreshToken();
      if (newToken) {
        return lookupCustomerByEmail(email, newToken, refreshToken, true);
      }
      throw new Error("Session expired");
    }
    if (response.status === 404) {
      return null;
    }
    throw new Error(`API error: ${response.status}`);
  }

  return response.json() as Promise<OpsMateCustomer>;
}

export async function submitDraft(
  conversationId: string,
  prompt: string,
  token: string,
  refreshToken: TokenRefresher
): Promise<PromptedDraftResponse> {
  const doRequest = async (t: string) => {
    const response = await fetch(`${WEBHOOK_BASE}/webhook/prompted-draft`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${t}`,
      },
      body: JSON.stringify({ conversationId, prompt }),
    });

    if (!response.ok) {
      if (response.status === 401) return null;
      const errData = await response.json().catch(() => ({}));
      throw new Error(
        (errData as { error?: string }).error ||
          `Server error: ${response.status}`
      );
    }

    return (await response.json()) as PromptedDraftResponse;
  };

  const result = await doRequest(token);
  if (result) return result;

  const newToken = await refreshToken();
  if (!newToken) throw new Error("Authentication failed");

  const retryResult = await doRequest(newToken);
  if (!retryResult) throw new Error("Authentication failed");

  return retryResult;
}
